import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { selectAllNews, selectNewsStatus } from '../../redux/newsSlice';
import { Box, Container, Typography, Button, Chip, Skeleton } from '@mui/material';

const HomeNewsCard = ({ item, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    style={{ width: '100%', display: 'contents' }}
    transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
  >
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        aspectRatio: '4 / 5',
        overflow: 'hidden',
        borderRadius: '25px',
        position: 'relative',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundImage: `url(${item.imageUrl})`,
        border: '1px solid rgba(0,0,0,0.05)',
        width: { xs: '95%', sm: "48%", md: "31%", xxl: "23%" },
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        '&:hover': {
          transform: 'translateY(-10px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          '& .card-overlay': {
            background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)',
          }
        }
      }}
    >
      <Box
        className="card-overlay"
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          p: 3,
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
          transition: 'all 0.4s ease',
        }}
      >
        {item?.date && (
          <Chip
            label={format(new Date(item.date), 'dd MMM yyyy')}
            size="small"
            sx={{
              position: 'absolute',
              top: 24,
              right: 24,
              bgcolor: '#fbbf24',
              color: '#000000',
              fontWeight: 700,
              borderRadius: '8px',
              fontSize: '0.75rem',
            }}
          />
        )}
        <Typography
          variant="h3"
          sx={{
            color: '#ffffff',
            fontFamily: 'Bebas Neue',
            fontSize: '1.8rem',
            lineHeight: 1.1,
            mb: 1,
            letterSpacing: '0.5px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.content}
        </Typography>
      </Box>
    </Box>
  </motion.div>
);

function LatestNews() {
  const navigate = useNavigate();
  const news = useSelector(selectAllNews);
  const status = useSelector(selectNewsStatus);

  const handleViewAll = () => navigate('/news');
  const isLoading = status === 'loading' || status === 'idle';

  if (!isLoading && (!news || news.length === 0)) return null;

  return (
    <Box
      sx={{
        bgcolor: '#ffffff',
        py: { xs: 10, md: 15 },
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(circle at 80% 20%, #f8fafc 0%, #ffffff 100%)'
      }}
    >
      {/* Background Decoration Text */}
      <Typography
        variant="h1"
        sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: { xs: '8rem', md: '15rem' },
          fontWeight: 900,
          color: 'transparent',
          WebkitTextStroke: '1px rgba(0,0,0,0.03)',
          userSelect: 'none',
          zIndex: 0,
          whiteSpace: 'nowrap',
          fontFamily: 'Bebas Neue'
        }}
      >
        NEWS
      </Typography>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ mb: { xs: 8, md: 12 }, textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{
              color: '#fbbf24',
              fontWeight: 700,
              letterSpacing: '3px',
              mb: 1,
              display: 'block'
            }}
          >
            Inside The Grid
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Bebas Neue',
              mb: 3,
              color: '#0f172a',
              fontSize: { xs: '3.5rem', md: '5rem' },
              letterSpacing: '2px',
            }}
          >
            Latest <Box component="span" sx={{ color: '#fbbf24' }}>Updates</Box>
          </Typography>
          <Box
            sx={{
              width: '80px',
              height: '4px',
              bgcolor: '#fbbf24',
              margin: '0 auto 24px',
              borderRadius: '2px'
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              color: '#64748b',
              maxWidth: '650px',
              margin: '0 auto',
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              lineHeight: 1.6
            }}
          >
            Stay updated with the latest breakthroughs, race results, and community highlights from the world of Auto Gymkhana.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-around" }}>
          {isLoading ? (
            [...Array(3)].map((_, index) => (
              <Box
                key={`skeleton-${index}`}
                sx={{
                  width: { xs: '95%', sm: "48%", md: "31%", xxl: "23%" },
                  aspectRatio: '4 / 5',
                  borderRadius: '25px',
                  mb: 3,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    p: 3,
                    zIndex: 1
                  }}
                >
                  <Skeleton width="60%" height={30} sx={{ mb: 1, bgcolor: 'rgba(0,0,0,0.05)' }} />
                  <Skeleton width="90%" height={20} sx={{ mb: 0.5, bgcolor: 'rgba(0,0,0,0.05)' }} />
                  <Skeleton width="80%" height={20} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
                </Box>
              </Box>
            ))
          ) : (
            news.slice(0, 3).map((item, index) => (
              <HomeNewsCard
                key={item.id}
                item={item}
                index={index}
                onClick={() => navigate(`/news/${item.id}`)}
              />
            ))
          )}
        </Box>

        {/* Bottom Call to Action */}
        <Box sx={{ mt: 10, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleViewAll}
            endIcon={<ArrowForwardIcon />}
            sx={{
              color: '#0f172a',
              borderColor: 'rgba(15, 23, 42, 0.2)',
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              borderRadius: '0',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#fbbf24',
                color: '#fbbf24',
                bgcolor: 'rgba(251, 191, 36, 0.05)',
                transform: 'translateY(-3px)'
              }
            }}
          >
            Explore all news
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default LatestNews;
